import moment from "moment";
import { Validity, ValidityMap } from "../store/validity/state";

export type Validator<T> = (value: T) => Validity;

export class ValidatorComposer<T> {
    constructor(protected validator: Validator<T>) {}

    public is(validator: Validator<T>): ValidatorComposer<T> {
        return new ValidatorComposer<T>(compose(this.validator, validator));
    }

    public onlyIf(condition: (value: T) => boolean): ValidatorComposer<T> {
        return new ValidatorComposer<T>(branch(this.validator, condition));
    }

    public run(value: T): Validity {
        return this.validator(value);
    }
}

function branch<T>(
    v: Validator<T>,
    condition: (value: T) => boolean,
): Validator<T> {
    return (value: T) => {
        if (condition(value)) {
            return v(value);
        } else {
            return emptyValidity;
        }
    };
}

export const check = new ValidatorComposer((value) => emptyValidity);

export function compose<T>(a: Validator<T>, b: Validator<T>): Validator<T> {
    return (value: T) => {
        const validA = a(value);
        const validB = b(value);
        const valid = combineValidity(validA, validB);
        valid.locked = validA.locked || validB.locked;
        return valid;
    };
}

export function composeAll<T>(validators: Validator<T>[]): Validator<T> {
    return validators.reduce(compose, () => emptyValidity);
}

export const emptyValidity = new Validity(true, null, false, false);

export function combineValidity(a: Validity, b: Validity): Validity {
    return {
        valid: a.valid && b.valid,
        message: a.message ? a.message : b.message,
        required: a.required || b.required,
        locked: false,
    };
}

export function combineValidities(validities: Validity[]) {
    return validities.reduce(combineValidity, emptyValidity);
}

export function combineValidityMap<T extends Validity>(
    validityMap: ValidityMap<T>,
): Validity {
    return combineValidities(Object.values(validityMap));
}

export function manualCheck(
    message: string,
    required: boolean,
    valid: boolean,
): Validity {
    return {
        valid: valid,
        message: valid ? null : message,
        required: required,
        locked: false,
    };
}

export function plain<T>(
    message: string,
    required: boolean,
    condition: (value: T) => boolean,
): Validator<T> {
    return (value: T) => {
        if (value === undefined || value === null) {
            return {
                valid: !required,
                message: required ? strings.required : null,
                required: required,
                locked: false,
            };
        } else {
            const valid = condition(value);
            return {
                valid: valid,
                message: !valid ? message : null,
                required: required,
                locked: false,
            };
        }
    };
}

export const strings = {
    required: "Dieser Wert wird benötigt.",
    notNull: "Dieses Feld darf nicht leer sein.",
    notEmpty: "Dieses Feld darf nicht leer sein.",
    validDate: "Dieses Feld muss ein gültiges Datum enthalten.",
    integer: "Dieser Wert muss eine ganze Zahl sein.",
    on: "Dieses Feld muss gesetzt sein.",
    off: "Dieses Feld darf nicht gesetzt sein.",
};

export var notNull = plain(
    strings.notNull,
    true,
    (value: any) => value != null && value !== "",
);
export var notEmpty = plain(
    strings.notEmpty,
    true,
    (value: string | any[]) =>
        value != null &&
        value != undefined &&
        value.length > 0 &&
        (value instanceof Array || !!value.trim()),
);
export var validDate = plain(
    strings.validDate,
    true,
    (value: string | any[]) =>
        value != null && moment(value, "YYYY-MM-DD").isValid(),
);
export var integer = plain(strings.integer, false, (value: number) =>
    Number.isInteger(value),
);
export var on = plain(strings.on, true, (value: boolean) => value);
export var off = plain(strings.off, true, (value: boolean) => !value);
